import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {CardAction} from "../../components/scotty-card/scotty-card.component";
import {Paginator} from "../../models/paginator";
import {Personnel} from "../../models/personnel";
import {Skill, SkillLevel} from "../../models/skill";
import {TableAction, TableColumn, TableColumnType, TableComponent} from "../../components/table/table.component";
import {SkillManagerService} from "../../services/skill-manager.service";
import {UiManagerService} from "../../services/ui-manager.service";
import {
  NewSkillDialogComponent,
  SkillDialogData
} from "../../components/dialogs/new-skill-dialog/new-skill-dialog.component";
import {environment} from "@environments/environment";
import {MatDialog} from "@angular/material/dialog";
import {
  NewSkillLevelDialogComponent,
  SkillLevelDialogData
} from "../../components/dialogs/new-skill-level-dialog/new-skill-level-dialog.component";

@Component({
  selector: 'app-skill-page',
  templateUrl: './skill-page.component.html',
  styleUrls: ['./skill-page.component.scss']
})
export class SkillPageComponent implements OnInit {

  isLoading = true;

  skills: Skill[]
  skillsPaginator: Paginator<Skill>;
  @ViewChild('skillsTable', { static: false })
  skillsTable: TableComponent;

  skillLevels: SkillLevel[];
  skillLevelsPaginator: Paginator<SkillLevel>;
  @ViewChild('skillLevelsTable', { static: false })
  skillLevelsTable: TableComponent;

  constructor(
    private skillManagerService: SkillManagerService,
    private uiManagerService: UiManagerService,
    private dialog: MatDialog,
  ) {
    this.skillsPaginator = new Paginator<Skill>();
    this.skillLevelsPaginator = new Paginator<SkillLevel>();
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([
      this.fetchSkills(),
      this.fetchSkillLevels()
    ]);
    this.isLoading = false;
  }

  async fetchSkills() {
    this.skillsPaginator.onPageResults = (skills: Skill[]) => {
      this.skills = skills;
      this.skillsTable?.refresh();
    }
    await this.skillManagerService.getAllSkills(this.skillsPaginator);
  }

  async fetchSkillLevels() {
    this.skillLevelsPaginator.onPageResults = (skillLevels: SkillLevel[]) => {
      this.skillLevels = skillLevels;
      this.skillLevelsTable?.refresh();
    }
    await this.skillManagerService.getAllSkillLevels(this.skillLevelsPaginator);
  }

  getSkillActions(): CardAction[] {
    return [
      {
        icon: 'add',
        name: 'New skill',
        callback: () => {

          const dialogRef = this.dialog.open(NewSkillDialogComponent, {
            width: '400px',
            data: { } as SkillDialogData
          });

          dialogRef.afterClosed().subscribe(async (skillData: SkillDialogData) => {
            if (skillData) {
              await this.skillManagerService.createSkill(skillData as Skill);
              await this.skillsPaginator.refresh();
            }
          });

        }
      },
    ];
  }

  getSkillLevelActions(): CardAction[] {
    return [
      {
        icon: 'add',
        name: 'New skill level',
        callback: () => {

          const dialogRef = this.dialog.open(NewSkillLevelDialogComponent, {
            width: '400px',
            data: { } as SkillLevelDialogData
          });

          dialogRef.afterClosed().subscribe(async (skillLevelData: SkillLevelDialogData) => {
            if (skillLevelData) {
              await this.skillManagerService.createSkillLevel(skillLevelData as SkillLevel);
              await this.skillLevelsPaginator.refresh();
            }
          });

        }
      },
    ];
  }

  getSkillsTableColumns(): TableColumn[] {
    return [
      { key: 'name', type: TableColumnType.DEFAULT, title: 'Name' },
      { key: 'validity', type: TableColumnType.DEFAULT, title: 'Validity' },
      /*{ key: 'color', type: TableColumnType.COLOR, title: 'Color' },*/
      { key: 'preview', type: TableColumnType.HTML, title: 'Preview' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getSkillsTableData() {
    return this.skills?.map(skill => {
      return {
        name: skill.name,
        validity: skill.validityInDays == 0 ? '-' : `${skill.validityInDays} days`,
        /*color: skill.color,*/
        preview: `<div class="chips"><span style="background-color: ${skill.color};">${skill.name}</span></div>`,
        actions: [
          {
            icon: 'edit', callback: () => {

              const dialogRef = this.dialog.open(NewSkillDialogComponent, {
                width: '400px',
                data: { name: skill.name, validityInDays: skill.validityInDays, color: skill.color } as SkillDialogData
              });

              dialogRef.afterClosed().subscribe(async (skillData: SkillDialogData) => {
                if (skillData) {
                  await this.skillManagerService.updateSkill(skill.id, skillData as Skill);
                  await this.skillsPaginator.refresh();
                }
              });

            }
          },
          {
            icon: 'delete', color: 'warn', callback: () => {

              this.uiManagerService.showConfirmDialog('Remove skill', 'Are you sure do you want to remove this skill?<br>This operation is irreversible.', async () => {
                await this.skillManagerService.removeSkill(skill.id);
                await this.skillsPaginator.refresh();
              });

              // TODO unbind all skills from users and equipment models

            }
          }
        ] as TableAction[],

      };
    }) || [];
  }

  getSkillLevelsTableColumns(): TableColumn[] {
    return [
      { key: 'name', type: TableColumnType.DEFAULT, title: 'Name' },
      { key: 'isTutor', type: TableColumnType.BOOLEAN, title: 'Is Tutor' },
      { key: 'isExpert', type: TableColumnType.BOOLEAN, title: 'Is Expert' },
      { key: 'actions', type: TableColumnType.ACTIONS, title: 'Actions' },
    ];
  }

  getSkillLevelsTableData() {
    return this.skillLevels?.map(skillLevel => {
      return {
        name: skillLevel.name,
        isTutor: skillLevel.isTutor,
        isExpert: skillLevel.isExpert,
        actions: [
          {
            icon: 'edit', callback: () => {

              const dialogRef = this.dialog.open(NewSkillLevelDialogComponent, {
                width: '400px',
                data: { name: skillLevel.name, isExpert: skillLevel.isExpert, isTutor: skillLevel.isTutor } as SkillLevelDialogData
              });

              dialogRef.afterClosed().subscribe(async (skillLevelData: SkillLevelDialogData) => {
                if (skillLevelData) {
                  await this.skillManagerService.updateSkillLevel(skillLevel.id, skillLevelData as SkillLevel);
                  await this.skillLevelsPaginator.refresh();
                }
              });

            }
          },
          {
            icon: 'delete', color: 'warn', callback: () => {

              this.uiManagerService.showConfirmDialog('Remove skill level', 'Are you sure do you want to remove this skill level?<br>This operation is irreversible.', async () => {
                await this.skillManagerService.removeSkillLevel(skillLevel.id);
                await this.skillLevelsPaginator.refresh();
              });

              // TODO unbind all skills from users and equipment models

            }
          }
        ] as TableAction[],

      };
    }) || [];
  }

}
